// app/(todos)/todos/components/TodoAuthLinks.tsx
'use client';

import { motion } from 'framer-motion';
import { ListTodo } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import AuthModal from '../../../components/forms/AuthModal';
// import { prefetchAuthChunks } from '../../../components/forms/prefetchAuthChunks';
import { Button } from '../../../components/ui/button';
import { prefetchAuthChunks } from '../../../components/auth/prefetchAuthChunks';

type AuthView = 'login' | 'register';

export default function TodoAuthLinks() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [openAuthModal, setOpenAuthModal] = useState(false);
  const [authView, setAuthView] = useState<AuthView>('login');

  const shouldOpenFromQuery = useMemo(() => searchParams.get('auth') === 'true', [searchParams]);

  useEffect(() => {
    if (!shouldOpenFromQuery) return;

    setAuthView('login');
    setOpenAuthModal(true);
    router.replace('/todos', { scroll: false });
  }, [shouldOpenFromQuery, router]);

  const prefetch = useCallback(() => {
    void prefetchAuthChunks();
  }, []);

  const openModal = useCallback((view: AuthView) => {
    void prefetchAuthChunks();
    setAuthView(view);
    setOpenAuthModal(true);
  }, []);

  const handleClose = useCallback(() => setOpenAuthModal(false), []);

  const handleSuccess = useCallback(() => {
    setOpenAuthModal(false);
    toast.success('Вы успешно вошли!');
    router.refresh();
  }, [router]);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="text-center bg-slate-800/50 p-6 sm:p-8 rounded-xl shadow-2xl max-w-2xl mx-auto"
        role="region"
        aria-labelledby="welcome-heading"
      >
        <div className="flex justify-center mb-4">
          <ListTodo className="w-12 h-12 text-sky-400" />
        </div>

        <h2 id="welcome-heading" className="text-2xl sm:text-3xl font-bold text-main mb-4">
          Ваш личный список дел
        </h2>

        <p className="text-slate-300 leading-relaxed max-w-lg mx-auto">
          Планируйте задачи, группируйте их по проектам и получайте уведомления на почту и в Telegram.
        </p>

        <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button
            onClick={() => openModal('login')}
            onPointerEnter={prefetch}
            onFocus={prefetch}
            onTouchStart={prefetch}
            className="w-full sm:w-auto bg-sky-600 hover:bg-sky-500 text-white font-semibold"
            aria-label="Войти в свой аккаунт"
          >
            Войти
          </Button>

          <span className="text-slate-400 hidden sm:inline">или</span>

          <Button
            onClick={() => openModal('register')}
            onPointerEnter={prefetch}
            onFocus={prefetch}
            onTouchStart={prefetch}
            variant="outline"
            className="w-full sm:w-auto border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
            aria-label="Создать новый аккаунт"
          >
            Зарегистрироваться
          </Button>
        </div>
      </motion.div>

      <AuthModal
        open={openAuthModal}
        onClose={handleClose}
        onSuccess={handleSuccess}
        redirectPath="/todos"
        initialView={authView}
      />
    </>
  );
}
